import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ValidityBadge } from '@/components/ValidityBadge';
import { useQueryClient } from '@tanstack/react-query';
import { ShieldCheck } from 'lucide-react';
import {
  AddButton, LiveScreen, QueryState, StatusPill, formatDate,
  useDebounced, type Column,
} from '@/components/live';
import { RecordDrawer } from '@/components/RecordDrawer';
import { DataGrid } from '@/components/DataGrid';
import { StatCards, applyStat } from '@/components/StatCards';
import { StatusChanger } from '@/components/StatusChanger';
import { DeleteRecordButton } from '@/components/DeleteRecordButton';
import { useRows } from '@/hooks/useResource';
import { callRpc, saveRow } from '@/api/resources';
import { FileUpload } from '@/components/FileUpload';
import { useAuth } from '@/auth/AuthProvider';
import { can } from '@/auth/session';
import { toast } from '@/lib/toast';
import { statusStats } from '@/lib/recordStatus';
import type { KycRow } from '@/types/records';
import { KycRecordDrawer } from '@/screens/parts/KycRecordDrawer';
import type { UploadedDocument } from '@/api/documents';

/**
 * KYC evidence per party.
 *
 * Three facts make a KYC record, and all three are required together: WHICH document, its
 * NUMBER, and how long it is VALID. A type with no number proves nothing, and a number with no
 * expiry silently becomes stale - the database derives `effective_status` from the validity
 * dates, so an expired document reports itself rather than waiting to be noticed.
 *
 * Maker and checker must differ: the database refuses to let the person who recorded a KYC
 * record verify it. That is enforced there, not here, and the refusal is shown as written.
 *
 * KYC is a list of child records per party - a farmer brings several documents, one per
 * record type - added through `KycRecordDrawer`, which takes the PDF/JPEG in the same form.
 * The scan is stored in the private `kyc-documents` bucket and attached by `document_id`. Viewing a scan produces a signed URL that expires in minutes -
 * nothing here ever holds a lasting link to somebody's identity document.
 */
export default function KycScreen() {
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const debounced = useDebounced(search);
  const [adding, setAdding] = useState(false);
  const [renewing, setRenewing] = useState<KycRow | null>(null);
  const [stat, setStat] = useState<string | null>(null);
  // The record open in the side drawer: its status, and its scan.
  const [attaching, setAttaching] = useState<KycRow | null>(null);

  const records = useRows<KycRow>('party_kyc_records', {
    search: debounced ? { columns: ['masked_document_no', 'issued_by'], term: debounced } : undefined,
    orderBy: { column: 'created_at', ascending: false },
  });

  // One click from Renewals due: the record to renew arrives as ?renew=<id> and the form opens
  // filled in from it.
  const [params, setParams] = useSearchParams();
  const renewId = params.get('renew');
  useEffect(() => {
    if (!renewId) return;
    const source = (records.data ?? []).find((row) => row.id === renewId);
    if (!source) return;
    setRenewing(source);
    setAdding(true);
    params.delete('renew');
    setParams(params, { replace: true });
  }, [renewId, records.data, params, setParams]);

  // The stored file behind an existing scan, so the drawer can offer to open it rather than
  // claiming nothing is attached.
  const attachedDocument = useRows<{ id: string; storage_bucket: string; storage_path: string; original_filename: string | null }>(
    'documents',
    { filters: { id: attaching?.document_id ?? undefined } },
    Boolean(attaching?.document_id),
  );

  const mayAdd = can(profile, 'party.kyc', 'create');
  const mayVerify = can(profile, 'party.kyc', 'approve');

  const statusOf = (row: KycRow) => row.effective_status ?? row.verification_status;
  const STATS = useMemo(() => [
    { key: 'all', label: 'KYC records', match: () => true, tone: 'neutral' as const },
    ...statusStats<KycRow>(statusOf),
    { key: 'expiring', label: 'Expiring in 30 days', tone: 'warn' as const,
      match: (row: KycRow) => row.days_to_expiry != null && row.days_to_expiry >= 0 && row.days_to_expiry <= 30 },
    { key: 'noscan', label: 'No scan', tone: 'error' as const, match: (row: KycRow) => !row.document_id },
  ], []);
  const visible = useMemo(() => applyStat(records.data ?? [], STATS, stat), [records.data, STATS, stat]);

  const columns: Array<Column<KycRow>> = [
    { key: 'kyc_type_label', label: 'Record type', render: (row) => row.kyc_type_label ?? '--' },
    { key: 'masked_document_no', label: 'Number', render: (row) => row.masked_document_no ?? '--' },
    { key: 'issued_by', label: 'Issued by', render: (row) => row.issued_by ?? '--' },
    { key: 'valid_to', label: 'Validity', render: (row) => (row.valid_to ? <ValidityBadge from={row.valid_from} to={row.valid_to} /> : <span className="muted">No expiry</span>) },
    {
      key: 'days_to_expiry', label: 'Expires in', align: 'right',
      render: (row) => (row.days_to_expiry === null || row.days_to_expiry === undefined
        ? '--'
        : row.days_to_expiry < 0
          ? <span style={{ color: 'var(--error)' }}>Expired</span>
          : `${row.days_to_expiry} days`),
    },
    { key: 'effective_status', label: 'Status', render: (row) => <StatusPill value={statusOf(row)} /> },
    {
      key: 'scan',
      label: 'Scan',
      render: (row) => (row.document_id
        ? <button onClick={(event) => { event.stopPropagation(); setAttaching(row); }} type="button">View</button>
        : mayAdd
          ? <button onClick={(event) => { event.stopPropagation(); setAttaching(row); }} type="button">Attach</button>
          : <span className="muted">--</span>),
    },
    {
      key: 'act',
      label: '',
      render: (row) => (row.verification_status === 'pending' && mayVerify ? (
        <button onClick={(event) => { event.stopPropagation(); void verify(row); }} type="button">
          <ShieldCheck size={14} /> Verify
        </button>
      ) : null),
    },
  ];

  /**
   * Verification goes through the one status RPC, which stamps the verifier from the session.
   * The maker/checker guard lives in the database and refuses when the verifier is the creator.
   */
  async function verify(row: KycRow) {
    try {
      await callRpc('fn_set_record_status', { p_entity: 'kyc', p_id: row.id, p_status: 'verified' });
      await queryClient.invalidateQueries({ queryKey: ['party_kyc_records'] });
      await queryClient.invalidateQueries({ queryKey: ['farmers'] });
      toast.success('Verified.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'That could not be verified.');
    }
  }

  /** Point the KYC record at the file that was just stored. */
  async function attachDocument(row: KycRow, document: UploadedDocument) {
    try {
      await saveRow('party_kyc_records', row.id, { document_id: document.id });
      await queryClient.invalidateQueries({ queryKey: ['party_kyc_records'] });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'The scan could not be attached to the record.');
    }
  }

  const openRecord = attaching ? (records.data ?? []).find((row) => row.id === attaching.id) ?? attaching : null;

  return (
    <LiveScreen
      actions={<AddButton disabled={!mayAdd} label="Add KYC record" onClick={() => setAdding(true)}
                          title={mayAdd ? undefined : 'Your role cannot record KYC.'} />}
      subtitle="Identity and land evidence per party. A farmer may hold several records, one per record type."
      title="Party KYC"
    >

      <StatCards active={stat} onSelect={setStat} rows={records.data ?? []} stats={STATS} />

      <QueryState empty="No KYC evidence has been recorded yet." query={records}>
        <DataGrid columns={columns} exportName="kyc-records" onRowClick={setAttaching}
                  onSearchChange={setSearch} search={search} searchPlaceholder="Document number or issuer"
                  rows={visible} screenKey="party.kyc" />
      </QueryState>

      <RecordDrawer
        record={openRecord ? { table: 'party.party_kyc_records', id: openRecord.id, invalidate: ['party_kyc_records'] } : null}
        footer={
          <>
            {openRecord && (
              <DeleteRecordButton entity="kyc" id={openRecord.id} invalidate={['party_kyc_records', 'farmers']}
                                  label={`${openRecord.kyc_type_label ?? 'KYC record'} ${openRecord.masked_document_no ?? ''}`.trim()}
                                  onDeleted={() => setAttaching(null)} screenKey="party.kyc" />
            )}
            <button className="primary" onClick={() => setAttaching(null)} type="button">Done</button>
          </>
        }
        onClose={() => setAttaching(null)}
        open={Boolean(openRecord)}
        subtitle={openRecord ? `${openRecord.kyc_type_label ?? 'KYC'} ${openRecord.masked_document_no ?? ''}` : undefined}
        title="KYC record"
      >
        {/* The record read first (owner, 2026-09-27: "shall show detailed view"); the status and the
            file below are the actions on it. Its fields are corrected by renewing, not by editing. */}
        {openRecord && (
          <dl className="detail-grid spec-view">
            <div><dt>Record type</dt><dd>{openRecord.kyc_type_label ?? '--'}</dd></div>
            <div><dt>Number</dt><dd>{openRecord.masked_document_no ?? '--'}</dd></div>
            <div><dt>Issued by</dt><dd>{openRecord.issued_by ?? '--'}</dd></div>
            <div><dt>Issued on</dt><dd>{openRecord.issued_on ? formatDate(openRecord.issued_on) : '--'}</dd></div>
            <div><dt>Valid</dt><dd>{openRecord.valid_to ? <ValidityBadge from={openRecord.valid_from} to={openRecord.valid_to} /> : 'No expiry'}</dd></div>
            <div><dt>Status</dt><dd><StatusPill value={statusOf(openRecord)} /></dd></div>
            <div><dt>Verified on</dt><dd>{openRecord.verified_at ? formatDate(openRecord.verified_at) : '--'}</dd></div>
          </dl>
        )}
        {openRecord && (
          <StatusChanger current={openRecord.verification_status} entity="kyc" id={openRecord.id}
                         invalidate={['party_kyc_records', 'farmers']} key={openRecord.id + openRecord.verification_status}
                         screenKey="party.kyc" />
        )}
        {openRecord?.rejection_reason && (
          <p className="muted" style={{ margin: 0 }}>Reason on record: {openRecord.rejection_reason}</p>
        )}
        {openRecord && profile?.organization && (
          <FileUpload
            accept="application/pdf,image/jpeg,image/png,image/webp"
            bucket="kyc-documents"
            documentTypeId={null}
            existing={attachedDocument.data?.[0] ?? null}
            label="Document file (PDF or JPEG)"
            onUploaded={(document: UploadedDocument) => void attachDocument(openRecord, document)}
            organizationId={profile.organization.id}
            ownerId={openRecord.party_id}
          />
        )}
      </RecordDrawer>

      <KycRecordDrawer onClose={() => { setAdding(false); setRenewing(null); }} open={adding} renewing={renewing} />
    </LiveScreen>
  );
}
